import { useEffect, useState, type ReactNode } from "react";
import { ThemeProvider, createTheme } from "@mui/material";

// MUI cannot consume CSS custom properties while creating its palette (its
// colour maths parses real colour strings), so these tables carry the values it
// must be able to parse, mirroring the PS1 skin (ui/ps1/ps1.css). There is one
// table per theme — night is the dark CRT screen, day the paper screen — and the
// provider follows `<html data-sky-theme>`. Anything that only has to *paint* a
// token (surfaces, frames, the gold fill) reads the CSS variable inside
// `styleOverrides` below instead, so it follows the theme without a table entry.
export const MATERIAL_PALETTES = {
  night: {
    mode: "dark",
    ink: "#F2F2EE",
    muted: "#C9CFDF",
    outline: "#2A3350",
    background: "#06070C",
    primary: "#F6C544",
    onPrimary: "#241A02",
    error: "#FF6B74",
    onError: "#1B0306",
  },
  day: {
    mode: "light",
    ink: "#1D1B26",
    muted: "#3D3A4A",
    outline: "#C2BBA8",
    background: "#E7E2D4",
    // Gold as text (a focused label, a text button) is the darkened gold ink.
    primary: "#7F5300",
    onPrimary: "#FFFDF8",
    error: "#B0141F",
    onError: "#FFFDF8",
  },
} as const;

/** The night table, kept under its old name for existing imports. */
export const MATERIAL_PALETTE = MATERIAL_PALETTES.night;

type Palette = (typeof MATERIAL_PALETTES)[keyof typeof MATERIAL_PALETTES];

const accent = "var(--busee-accent)";

function buildTheme(colors: Palette) {
  return createTheme({
    palette: {
      mode: colors.mode,
      // Declare every shade MUI may ask for. Leaving `light` / `dark` absent
      // makes MUI derive a second teal and red at runtime, which would fork the
      // canonical token families in index.css again.
      primary: {
        light: colors.primary,
        main: colors.primary,
        dark: colors.primary,
        contrastText: colors.onPrimary,
      },
      error: {
        light: colors.error,
        main: colors.error,
        dark: colors.error,
        contrastText: colors.onError,
      },
      // `paper` is a card resting on the sky: the scene's translucent card
      // colour. Popovers, menus and dialogs are opaque (see their overrides).
      background: { default: colors.background, paper: "var(--busee-surface)" },
      text: { primary: colors.ink, secondary: colors.muted },
      divider: colors.outline,
    },
    // PS1: square corners and the dot face (one weight, so no bold steps).
    shape: { borderRadius: 0 },
    typography: {
      fontFamily: "var(--ps1-font)",
      fontWeightRegular: 400,
      fontWeightMedium: 400,
      fontWeightBold: 400,
      h4: {
        fontSize: "1.75rem",
        lineHeight: "2rem",
        fontWeight: 600,
        letterSpacing: "-0.015em",
        fontVariantNumeric: "tabular-nums",
      },
      h5: {
        fontSize: "1.5rem",
        lineHeight: "1.875rem",
        fontWeight: 600,
        letterSpacing: "-0.015em",
      },
      subtitle1: { fontSize: "1rem", lineHeight: "1.375rem", fontWeight: 600 },
      body1: { fontSize: "0.9375rem", lineHeight: "1.3125rem", fontWeight: 400 },
      body2: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: 400 },
      caption: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: 400 },
      button: { fontSize: "0.8125rem", lineHeight: "1.125rem", fontWeight: 500 },
      overline: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: 500 },
    },
    components: {
      MuiButtonBase: {
        defaultProps: { disableRipple: false },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `2px solid ${accent}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 40,
            borderRadius: 0,
            textTransform: "none",
            fontWeight: 400,
            "&.MuiButton-contained": { border: "2px solid #ffe08a", backgroundColor: "var(--ps1-gold-fill)", color: "var(--ps1-gold-ink)" },
            "&.MuiButton-outlined, &.MuiButton-text": { border: "2px solid var(--ps1-orange)" },
            "@media (pointer: coarse)": { minHeight: 44 },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: 40,
            height: 40,
            "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: { colorPrimary: { color: accent } },
      },
      MuiChip: {
        defaultProps: { variant: "filled" },
        styleOverrides: {
          root: {
            height: 32,
            borderRadius: 0,
            fontWeight: 400,
            border: "1px solid var(--ps1-line)",
            backgroundColor: "var(--busee-fill-2)",
            color: colors.ink,
            "&.MuiChip-clickable:hover": { backgroundColor: "var(--busee-surface-hover)" },
            // Selected = the PS1 gold command with dark ink.
            "&.MuiChip-colorPrimary": {
              backgroundColor: "var(--ps1-gold)",
              borderColor: "var(--ps1-gold)",
              color: "var(--ps1-gold-ink)",
            },
          },
        },
      },
      MuiTextField: { defaultProps: { variant: "filled" } },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.Mui-focused": {
              outline: `2px solid ${accent}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiFilledInput: {
        defaultProps: { disableUnderline: true },
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: "2px solid var(--ps1-orange)",
            backgroundColor: "var(--busee-field)",
            "&:hover": { backgroundColor: "var(--busee-field)" },
            "&.Mui-focused": { backgroundColor: "var(--busee-field)", borderColor: "var(--ps1-gold)" },
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      // Anything that floats is opaque: translucency is for cards on the sky.
      MuiPopover: { styleOverrides: { paper: { backgroundColor: "var(--busee-overlay-panel)", border: "2px solid var(--ps1-orange)" } } },
      MuiMenu: { styleOverrides: { paper: { backgroundColor: "var(--busee-overlay-panel)", border: "2px solid var(--ps1-orange)" } } },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover, &.Mui-focusVisible": { backgroundColor: "var(--busee-surface-hover)" },
            "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "var(--ps1-gold-fill)", color: "var(--ps1-gold-ink)" },
          },
        },
      },
      MuiDialog: { styleOverrides: { paper: { backgroundColor: "var(--busee-overlay-panel)", border: "2px solid var(--ps1-orange)" } } },
    },
  });
}

/**
 * The Eleven skin (ui/eleven/eleven.css): light, hairline frames, 10px controls,
 * the platform sans for text. Colours that only paint read the skin's tokens, so
 * the Light and Warm directions share this one theme.
 */
function buildElevenTheme() {
  const ink = "#0f0f10";
  const line = "var(--el-line)";
  const control = 10;
  return createTheme({
    palette: {
      mode: "light",
      primary: { light: ink, main: ink, dark: ink, contrastText: "#ffffff" },
      error: { light: "#d93036", main: "#d93036", dark: "#d93036", contrastText: "#ffffff" },
      background: { default: "#ffffff", paper: "var(--el-card)" },
      text: { primary: ink, secondary: "rgba(0, 0, 0, 0.53)" },
      divider: "rgba(0, 0, 0, 0.075)",
    },
    shape: { borderRadius: control },
    typography: {
      fontFamily: "var(--el-font-body)",
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h4: { fontFamily: "var(--el-font-head)", fontSize: "1.75rem", lineHeight: "2.25rem", fontWeight: 500 },
      h5: { fontFamily: "var(--el-font-head)", fontSize: "1.375rem", lineHeight: "1.875rem", fontWeight: 500 },
      subtitle1: { fontSize: "0.9375rem", lineHeight: "1.375rem", fontWeight: 500 },
      body1: { fontSize: "0.875rem", lineHeight: "1.3125rem" },
      body2: { fontSize: "0.8125rem", lineHeight: "1.125rem" },
      caption: { fontSize: "0.75rem", lineHeight: "1rem" },
      button: { fontSize: "0.8125rem", lineHeight: "1.125rem", fontWeight: 500 },
      overline: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: 500, letterSpacing: 0 },
    },
    transitions: {
      easing: { easeOut: "cubic-bezier(0.2, 0.8, 0.2, 1)", easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)" },
      duration: { shortest: 120, shorter: 160, short: 200, standard: 240, enteringScreen: 220, leavingScreen: 160 },
    },
    components: {
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: { root: { "&.Mui-focusVisible": { outline: "2px solid var(--el-ring)", outlineOffset: 2 } } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 32,
            borderRadius: control,
            textTransform: "none",
            fontWeight: 500,
            transition: "background-color 140ms, box-shadow 140ms, transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            "&:active": { transform: "scale(0.97)" },
            "&.MuiButton-contained": { backgroundColor: "var(--el-accent)", color: "var(--el-accent-fg)" },
            "&.MuiButton-contained:hover": { backgroundColor: "var(--el-accent-hover)" },
            "&.MuiButton-outlined": { border: `1px solid ${line}`, backgroundColor: "var(--el-card)", color: ink },
            "&.MuiButton-outlined:hover": { backgroundColor: "var(--el-hover)" },
            "&.MuiButton-text:hover": { backgroundColor: "var(--el-hover)" },
            "@media (pointer: coarse)": { minHeight: 44 },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: 32,
            height: 32,
            borderRadius: control,
            transition: "background-color 140ms, transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            "&:hover": { backgroundColor: "var(--el-hover)" },
            "&:active": { transform: "scale(0.92)" },
            "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 },
          },
        },
      },
      MuiChip: {
        defaultProps: { variant: "filled" },
        styleOverrides: {
          root: {
            height: 28,
            borderRadius: 8,
            fontWeight: 500,
            backgroundColor: "var(--el-alpha-50)",
            color: "var(--el-fg-2)",
            transition: "background-color 140ms, color 140ms, transform 120ms",
            "&.MuiChip-clickable:hover": { backgroundColor: "var(--el-alpha-100)", color: ink },
            "&.MuiChip-clickable:active": { transform: "scale(0.96)" },
            "&.MuiChip-colorPrimary": { backgroundColor: "var(--el-accent)", color: "var(--el-accent-fg)" },
          },
        },
      },
      MuiTextField: { defaultProps: { variant: "filled" } },
      MuiFilledInput: {
        defaultProps: { disableUnderline: true },
        styleOverrides: {
          root: {
            borderRadius: control,
            border: `1px solid ${line}`,
            backgroundColor: "var(--el-card)",
            transition: "border-color 140ms, box-shadow 160ms",
            "&:hover": { backgroundColor: "var(--el-card)", borderColor: "var(--el-line-strong)" },
            "&.Mui-focused": {
              backgroundColor: "var(--el-card)",
              borderColor: "var(--el-line-strong)",
              boxShadow: "0 0 0 4px var(--el-ring-soft)",
            },
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiPopover: {
        styleOverrides: {
          paper: { backgroundColor: "var(--el-card)", border: `1px solid ${line}`, borderRadius: 14, boxShadow: "var(--el-shadow-pop)" },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { backgroundColor: "var(--el-card)", border: `1px solid ${line}`, borderRadius: 14, boxShadow: "var(--el-shadow-pop)" },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 32,
            borderRadius: 8,
            fontSize: "0.875rem",
            "&:hover, &.Mui-focusVisible": { backgroundColor: "var(--el-hover)" },
            "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "var(--el-alpha-50)", color: ink },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: "var(--el-card)", border: `1px solid ${line}`, borderRadius: 20, boxShadow: "var(--el-shadow-pop)" },
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: ink, borderRadius: 8, fontSize: "0.75rem", fontWeight: 500 } },
      },
    },
  });
}

const THEMES = {
  night: buildTheme(MATERIAL_PALETTES.night),
  day: buildTheme(MATERIAL_PALETTES.day),
  eleven: buildElevenTheme(),
};

type ThemeName = keyof typeof THEMES;

function currentTheme(): ThemeName {
  // The JRPG field skin draws dark windows in both themes; only its sky follows day/night.
  if (document.documentElement.dataset.skin === "jrpg") return "night";
  if (document.documentElement.dataset.skin === "eleven") return "eleven";
  const attr = document.documentElement.dataset.skyTheme;
  if (attr === "day" || attr === "night") return attr;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "day" : "night";
}

/** Follows `<html data-sky-theme>`, which the sky runtime rewrites, and `data-skin`, which the shell sets per route. */
function useSkyTheme(): ThemeName {
  const [theme, setTheme] = useState<ThemeName>(currentTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(currentTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-sky-theme", "data-skin"] });
    setTheme(currentTheme());
    return () => observer.disconnect();
  }, []);
  return theme;
}

export function MaterialAppProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider theme={THEMES[useSkyTheme()]}>
      <div data-material-app="" className="h-full min-h-0">
        {children}
      </div>
    </ThemeProvider>
  );
}
